import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { GalleryCard, CarouselNav, CarouselDots } from '@/components/ds';
import { getSfSymbolIcon } from '@/lib/sf-symbols';
import { useSectionVisibility } from '@/hooks/useSectionVisibility';
import { useCarouselTracking } from '@/hooks/useCarouselTracking';
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';

interface InvestmentPill {
  icon: string;
  label: string;
}

interface InvestmentItem {
  title: string;
  subtitle?: string | null;
  image: string;
  pills?: InvestmentPill[];
}

interface InvestGallerySectionProps {
  id?: string;
  title: string;
  subtitle: string;
  items: InvestmentItem[];
  description?: string;
  className?: string;
  isLoading?: boolean;
}

const InvestGallerySection = ({ id, title, subtitle, items, description, className, isLoading }: InvestGallerySectionProps) => {
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [showNav, setShowNav] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // ⚠️ ANALYTICS: Track viewed cards to prevent duplicate events
  const viewedCards = useRef<Set<string>>(new Set());
  const hoverTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // ⚠️ ANALYTICS: Map id to display name
  const sectionId = id || 'invest-gallery';
  const sectionNameMap: Record<string, string> = {
    portfolios: 'Portfolios',
    funds: 'Funds',
    deals: 'Deals',
  };
  const sectionName = sectionNameMap[sectionId] || sectionId;
  const positionMap: Record<string, number> = {
    portfolios: 1,
    funds: 2,
    deals: 3,
  };

  // ⚠️ ANALYTICS: Section visibility tracking
  const { ref: sectionRef, inView: sectionInView } = useSectionVisibility({
    sectionId: sectionId,
    sectionName: sectionName,
    pageName: 'Invest',
    position: positionMap[sectionId] || 1,
  });

  // ⚠️ ANALYTICS: Carousel tracking
  const {
    trackCarouselViewed,
    trackCarouselScrolled,
  } = useCarouselTracking({
    carouselId: `${sectionId}-carousel`,
    section: sectionId,
    pageName: 'Invest',
    totalItems: items?.length || 0,
  });

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();

    // ⚠️ ANALYTICS: Track carousel scroll
    if (newIndex !== selectedIndex) {
      trackCarouselScrolled(selectedIndex, newIndex, 'drag');
    }

    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(newIndex);
  }, [emblaApi, selectedIndex, trackCarouselScrolled]);

  // Check if carousel needs navigation (content overflows viewport)
  const checkNavVisibility = useCallback(() => {
    if (!emblaApi) return;
    // If we can scroll in either direction, nav is needed
    const canScroll = emblaApi.canScrollPrev() || emblaApi.canScrollNext();
    setShowNav(canScroll);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    
    onSelect();
    checkNavVisibility();
    
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('reInit', checkNavVisibility);
    
    // Also check on resize
    window.addEventListener('resize', checkNavVisibility);
    
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
      emblaApi.off('reInit', checkNavVisibility);
      window.removeEventListener('resize', checkNavVisibility);
    };
  }, [emblaApi, onSelect, checkNavVisibility]);

  // ⚠️ ANALYTICS: Track carousel view when section is visible
  useEffect(() => {
    if (sectionInView && emblaApi && !isLoading && items && items.length > 0) {
      const visibleCount = emblaApi.slidesInView().length;
      trackCarouselViewed(visibleCount);
    }
  }, [sectionInView, emblaApi, isLoading, items, trackCarouselViewed]);

  // ⚠️ ANALYTICS: Track card viewed (2-second hover threshold — consolidated Content Viewed event)
  const handleCardMouseEnter = useCallback((item: InvestmentItem, index: number) => {
    const cardKey = `${sectionId}-${index}`;
    
    if (viewedCards.current.has(cardKey)) return;
    
    const timer = setTimeout(() => {
      if (!viewedCards.current.has(cardKey)) {
        viewedCards.current.add(cardKey);
        trackEvent(ANALYTICS_EVENTS.CONTENT_VIEWED, {
          content_id: cardKey,
          content_title: item.title,
          content_type: 'card',
          content_category: 'card',
          content_section: sectionId,
          content_page: 'Invest',
          content_position: index,
          total_cards: items?.length || 0,
          tracking_method: 'hover',
        });
      }
    }, 2000);
    
    hoverTimers.current.set(cardKey, timer);
  }, [sectionId, items]);

  const handleCardMouseLeave = useCallback((index: number) => {
    const cardKey = `${sectionId}-${index}`;
    const timer = hoverTimers.current.get(cardKey);
    if (timer) {
      clearTimeout(timer);
      hoverTimers.current.delete(cardKey);
    }
  }, [sectionId]);

  // Loading skeleton
  if (isLoading) {
    return (
      /* atom-surface-section-default: bg-background */
      <section className={cn("bg-background py-16 md:py-24 overflow-hidden", className)}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-md">
              <div className="h-12 w-64 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </div>
        <div className="pl-4 md:pl-8 lg:pl-[max(2rem,calc((100vw-1400px)/2+2rem))]">
          <div className="flex gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="w-[320px] md:w-[360px] lg:w-[400px] flex-shrink-0">
                <div className="h-[580px] md:h-[640px] lg:h-[680px] bg-muted animate-pulse rounded-3xl" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    /* atom-surface-section-default: bg-background */
    <section ref={sectionRef} id={id} className={cn("bg-background py-16 md:py-24 overflow-hidden scroll-mt-24", className)}>
      {/* Header Row - contained */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {/* molecule-section-header: header with subheader below */}
        <div className="mb-12 max-w-xl">
          {/* atom-text-section-header + atom-text-section-header-emphasis */}
          <h2 className="text-3xl font-light md:text-4xl lg:text-5xl text-foreground">
            <span>{title}</span>
            <br />
            <span className="font-semibold">{subtitle}</span>
          </h2>

          {/* atom-text-section-subheader: below header */}
          {description && (
            <p className="mt-4 text-17 text-muted-foreground leading-relaxed">
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Carousel - extends to right edge */}
      <div className="pl-4 md:pl-8 lg:pl-[max(2rem,calc((100vw-1400px)/2+2rem))]">
        <div className="overflow-visible" ref={emblaRef}>
          <div className="flex gap-4 embla-gesture-lock">
            {items.map((item, index) => {
              // Create tags from pills for detailed variant
              const tags = item.pills?.map((pill) => {
                const IconComponent = getSfSymbolIcon(pill.icon);
                return {
                  label: pill.label,
                  icon: IconComponent ? <IconComponent className="h-4 w-4" strokeWidth={2.5} /> : undefined,
                };
              }) || [];

              return (
                <div
                  key={`${sectionId}-${index}`}
                  className={`flex-shrink-0 ${
                    index === items.length - 1
                      ? "mr-4 md:mr-8 lg:mr-[max(2rem,calc((100vw-1400px)/2+2rem))]"
                      : ""
                  }`}
                  onMouseEnter={() => handleCardMouseEnter(item, index)}
                  onMouseLeave={() => handleCardMouseLeave(index)}
                >
                  {/* component-card-gallery-detailed */}
                  <GalleryCard
                    imageUrl={item.image}
                    title={item.title}
                    subtitle={item.subtitle || undefined}
                    variant="detailed"
                    tags={tags}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Navigation - mobile dots, desktop arrows */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {/* Mobile: Centered pagination dots */}
        <div className="mt-6 flex justify-center md:hidden">
          <CarouselDots
            selectedIndex={selectedIndex}
            totalSlides={items.length}
            onDotClick={scrollTo}
          />
        </div>

        {/* Desktop: Right-aligned arrows - only visible if carousel can scroll */}
        {showNav && (
          <div className="hidden md:flex mt-6 justify-end">
            <CarouselNav
              onPrev={scrollPrev}
              onNext={scrollNext}
              canScrollPrev={canScrollPrev}
              canScrollNext={canScrollNext}
              variant="subdued"
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default InvestGallerySection;
